import * as React from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import { ILesson, ICurrency } from './types';
import { colors } from '../styles/variables';
import t from './locales';
import { TranslationKeys } from './locales';
import Button from '../Shared/Button';
import Price from '../Shared/Price';
import { getImage } from '../utils/pathHelper';
import { minPrice } from '../utils/priceHelper';
import clockImage from './images/line_icon/clock.svg';

const LessonItemView = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  padding: 8px;
  border: 1px solid ${colors.gray300};
  border-radius: 4px;
  margin-bottom: 16px;
  color: ${colors.gray800};
  @media (min-width: 1000px) {
    flex-wrap: nowrap;
  }
  & > .left-column {
    display: flex;
    align-items: center;
    flex: none;
    width: 100%;
    margin-bottom: 8px;
    border-bottom: 1px solid ${colors.gray300};
    padding-bottom: 8px;
    @media (min-width: 1000px) {
      flex: 1;
      width: auto;
      margin-bottom: 0;
      border-bottom: 0;
      padding-bottom: 0;
    }
    .image {
      height: 80px;
      width: 80px;
      background-color: ${colors.blue};
      border-radius: 8px;
      flex: none;
      text-align: center;
      img {
        width: 40px;
        height: 40px;
        display: block;
        margin: 8px auto 0;
      }
      p {
        margin: 0;
        font-weight: 900;
        font-size: 14px;
        color: ${colors.white};
      }
    }
    .content {
      line-height: 1.5em;
      padding: 0 16px;
      flex: 1;
      p {
        font-size: 14px;
        margin: 0;
      }
    }
  }
  & > .right-column {
    display: flex;
    align-items: center;
    text-align: right;
    width: 100%;
    @media (min-width: 1000px) {
      padding: 8px;
      width: auto;
    }
  }
`;

const LessonItemBadge = styled.span`
  padding: 4px 6px;
  background-color: ${colors.gray100};
  border: 1px solid ${colors.gray600};
  color: ${colors.gray800};
  border-radius: 2px;
  font-size: 10px;
  margin-right: 4px;
  white-space: nowrap;
`;

const LessonItemBadges: React.FC<{ lesson: ILesson }> = ({ lesson }) => {
  return (
    <div style={{ marginTop: 4 }}>
      {
        ['languages', 'subjects', 'participantTypes'].map(label => {
          const selectedValues = lesson[label];
          if (selectedValues == null) { return null; }
          return (
            selectedValues.map((value: TranslationKeys) => (
              <LessonItemBadge key={`${lesson.snowItemId}-${value}`}>
                {t[value]()}
              </LessonItemBadge>
            ))
          );
        })
      }
    </div>
  )
};

type ILessonItem = {
  lesson: ILesson,
  selectedDateList: Date[],
  localCurrency: ICurrency,
}

const LessonItem: React.FC<ILessonItem> = ({ lesson, selectedDateList, localCurrency }) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const { snowItemId, name, priceTable } = lesson;
  const price = minPrice({ priceTable, selectedDateList });
  let priceToDisplay = null;
  // グループ料金 > 大人料金 > 子供料金の順で優先する
  if (price.childPrice && price.childFullPrice) {
    priceToDisplay = [price.childPrice, price.childFullPrice];
  }
  if (price.adultPrice && price.adultFullPrice) {
    priceToDisplay = [price.adultPrice, price.adultFullPrice];
  }
  if (price.groupPrice && price.groupFullPrice) {
    priceToDisplay = [price.groupPrice, price.groupFullPrice];
  }
  return (
    <LessonItemView>
      <div className="left-column">
        <div className="image">
          <img src={getImage(clockImage)} />
          <p>{lesson.requiredMinutes} min</p>
        </div>
        <div className="content">
          <p>{name}</p>
          <LessonItemBadges lesson={lesson} />
        </div>
      </div>
      <div className="right-column">
        {priceToDisplay &&
          <Price
            price={priceToDisplay[0]}
            fullPrice={priceToDisplay[1]}
            showDiscount
            localCurrency={localCurrency}
            style={{ marginRight: 16 }}
            showJPY={true}
            wavyLine={true} />
        }
        <Button href={ `/snow/items/${snowItemId}`} isLoading={isLoading} onClick={() => { setLoading(true); }}>
          {t.book()}
        </Button>
      </div>
    </LessonItemView>
  )
};

export default LessonItem
